import { LocalDateTime } from 'js-joda';
import { LocationDescriptor } from 'history';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { stringify } from '@tock/query-string';
import _ from 'lodash';

import { IWithContextProps, consumer, core, withContext } from '@tock/coretypes';
import { LockOptions } from 'types';
import { getEventBookUrl } from 'utils/experience';
import {
  offeringConsumerDetailPageRelativeLink,
  offeringConsumerMenuPageRelativeLink,
} from '@tock/shared/utils/offerings';
import { tryLockAndContinue } from 'utils/lock';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ExperienceListRoute, {
  MatchParams as ExperienceListMatchParams,
} from 'routes/business/search/ExperienceListRoute';
import ExperienceMenuRoute, {
  MatchParams as ExperienceMenuMatchParams,
} from 'routes/business/search/ExperienceMenuRoute';
import ExperienceRoute, {
  MatchParams as ExperienceMatchParams,
} from 'routes/business/search/ExperienceRoute';
import ExperienceSearchRoute from 'routes/business/search/ExperienceSearchRoute';
import PrivateDiningRoute from 'routes/business/search/PrivateDiningRequestRoute';
import SearchModalLockContent from 'components/SearchModalLockContent';
import TockHistory from 'utils/TockHistory';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';
import getJceWebBaseUrl from 'utils/jceUtils';

interface MatchParams {
  businessDomain: string;
  widget?: string;
  offering?: string;
}

interface Props extends RouteComponentProps<MatchParams> {
  backUrl?: string;
}

interface ConnectProps extends DefaultConnectProps {
  calendar: StoreType['calendar'];
  lock: StoreType['lock'];
  history: TockHistory;
}

interface State {
  isLocking: boolean;
  isSearching: boolean;
}

export interface PropsToPass {
  isSearching: boolean;
  isWidget: boolean;
  getUrl: (experience: core.TicketType, type: 'EXPERIENCE' | 'MENU') => string;
  onClose: () => void;
  onSearch: (location: LocationDescriptor) => void;
  onSelectTime: (
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    business: consumer.IBusinessConfig
  ) => Promise<void>;
}

class SearchModalRoute extends BaseComponent<Props & IWithContextProps & ConnectProps, State> {
  constructor(props: Props & IWithContextProps & ConnectProps) {
    super(props);

    this.state = {
      isLocking: false,
      isSearching: false,
    };
  }

  getBaseSearchUrl(experience: core.TicketType, type: 'EXPERIENCE' | 'MENU'): string {
    if (_.endsWith(this.props.match.path, 'book')) {
      return getEventBookUrl(experience);
    } else if (type === 'EXPERIENCE') {
      return offeringConsumerDetailPageRelativeLink(experience);
    } else {
      return offeringConsumerMenuPageRelativeLink(experience);
    }
  }

  handleClose() {
    if (BROWSER && window.RunningInWidget) {
      window.parent.postMessage(
        {
          source: 'tock',
          action: 'close',
        },
        '*'
      );
    } else if (this.props.backUrl) {
      this.props.history.pathPopPush(1, true, true);
    } else {
      this.props.replaceRouteWith('/');
    }
  }

  handleSearch(routeObject: LocationDescriptor) {
    this.setState({
      isSearching: true,
    });

    // Note: We are simulating loading here to give a visual indication that the
    // search results have changed. 17ms ~= 1 frame at 60 FPS, enough to be
    // noticable and to make sure React doesn't short-circuit the render.
    requestAnimationFrame(() => {
      this.props.replaceRouteWith(routeObject);

      this.setState({
        isSearching: false,
      });
    });
  }

  showConfirmAfterLock() {
    this.setState({
      isLocking: false,
    });
    this.props.actions.ui.setConfirmSelectionDialogVisible(true);
  }

  getVenueLocationSlug(business: consumer.IBusinessConfig) {
    const city = business.city || '';
    const state = business.state || '';
    if (!city || !state) {
      return 'chicago-il';
    }
    return `${_.kebabCase(city)}-${_.kebabCase(state)}`;
  }

  redirectWidgetToInitiateCheckout(
    business: consumer.IBusinessConfig,
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number
  ) {
    const query = stringify({
      partySize: selectedSize,
      dateTime: dateTime.toString(),
      venueSlug: business.domainName,
      venueLocation: this.getVenueLocationSlug(business),
      unitPriceCents: _.isNumber(priceCents) ? priceCents : 0,
      experienceId: ticketTypeId,
    });

    window.parent.postMessage(
      {
        source: 'tock',
        action: 'redirect',
        redirectTo: `${getJceWebBaseUrl()}/checkout/initiate-checkout?${query}`,
      },
      '*'
    );
  }

  async handleSelectTime(
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions
  ) {
    const { actions, lock } = this.props;
    const business = this.props.app?.config?.business;
    const isWidget = !_.isUndefined(this.props.match.params.widget);
    const isWidgetJceRedirectEnabled = this.props.context.isFeatureEnabled('TOCK_WIDGET_JCE');

    if (isWidget && isWidgetJceRedirectEnabled && business) {
      this.redirectWidgetToInitiateCheckout(
        business,
        ticketTypeId,
        selectedSize,
        dateTime,
        priceCents
      );
      return;
    }

    this.setState({
      isLocking: true,
    });

    // Clear all existing locks upon selecting a new individual time
    if (lock.currentLock || (lock.remainingLocks?.length ?? 0) > 0) {
      actions.lock.clear();
      if (lock.currentLock) {
        business?.id &&
          (await actions.lock.unlockTickets(
            {
              ticketSubsetId: lock.currentLock.ticketSubset[0].id,
              ticketGroupId: lock.currentLock.ticketSubset[0].ticketGroupId,
            },
            business?.id
          ));
      }
      // Clear remaining locks in a multi-checkout scenario
      const { remainingLocks } = this.props.lock;
      remainingLocks?.forEach((lock) => {
        this.props.actions.lock.unlockTickets({
          ticketSubsetId: lock.ticketSubset[0]?.id,
          ticketGroupId: lock.ticketSubset[0]?.ticketGroupId,
        });
      });
    }

    try {
      business &&
        (await tryLockAndContinue(
          this.props.actions,
          ticketTypeId,
          selectedSize,
          this.props.lock,
          dateTime,
          priceCents,
          {
            ...options,
            ticketTypeAccessToken: this.props.calendar.ticketTypeAccessToken,
          },
          business
        ));
      if (isWidget) {
        this.showConfirmAfterLock();
      } else {
        business && this.props.history.push(`/${business.domainName}/checkout/edit-guest-profile`);
      }
    } catch (error) {
      this.setState({
        isLocking: false,
      });
      throw error;
    }
  }

  renderExperienceSearch(propsToPass: PropsToPass) {
    return (routerProps: RouteComponentProps) => (
      <ExperienceSearchRoute {...routerProps} {...propsToPass} />
    );
  }

  renderPrivateDining(propsToPass: PropsToPass) {
    return (routerProps: RouteComponentProps) => (
      <PrivateDiningRoute {...routerProps} {...propsToPass} />
    );
  }

  renderExperience(propsToPass: PropsToPass) {
    return ((routerProps: RouteComponentProps<ExperienceMatchParams>) => (
      <ExperienceRoute {...routerProps} {...propsToPass} />
      // NOTE: Cast below was needed when we upgraded @types/react-router-dom.
    )) as (routerProps: RouteComponentProps) => JSX.Element;
  }

  renderExperienceMenu(propsToPass: PropsToPass) {
    return ((routerProps: RouteComponentProps<ExperienceMenuMatchParams>) => (
      <ExperienceMenuRoute {...routerProps} {...propsToPass} />
      // NOTE: Cast below was needed when we upgraded @types/react-router-dom.
    )) as (routerProps: RouteComponentProps) => JSX.Element;
  }

  renderExperiences(propsToPass: PropsToPass) {
    return ((routerProps: RouteComponentProps<ExperienceListMatchParams>) => (
      <ExperienceListRoute {...routerProps} {...propsToPass} />
      // NOTE: Cast below was needed when we upgraded @types/react-router-dom.
    )) as (routerProps: RouteComponentProps) => JSX.Element;
  }

  render() {
    const { isLocking, isSearching } = this.state;
    const { match } = this.props;
    const isWidget = !_.isUndefined(match.params.widget);

    const propsToPass: PropsToPass = {
      isSearching,
      isWidget,
      getUrl: this.getBaseSearchUrl,
      onClose: this.handleClose,
      onSearch: this.handleSearch,
      onSelectTime: this.handleSelectTime,
    };

    return (
      <>
        <Switch>
          {_.endsWith(match.path, 'book') ? (
            <Route path={match.path} render={this.renderExperience(propsToPass)} />
          ) : (
            false
          )}
          {_.endsWith(match.path, 'search') ? (
            <Route path={match.path} render={this.renderExperienceSearch(propsToPass)} />
          ) : (
            false
          )}
          {_.endsWith(match.path, 'private-dining') ? (
            <Route path={match.path} render={this.renderPrivateDining(propsToPass)} />
          ) : (
            false
          )}
          {_.endsWith(match.path, 'experiences') ? (
            <Route path={match.path} render={this.renderExperiences(propsToPass)} />
          ) : (
            false
          )}
          {match.params.offering && (
            <Route
              path={`${match.path}/private/:ticketTypeAccessToken`}
              render={this.renderExperience(propsToPass)}
            />
          )}
          {match.params.offering && (
            <Route
              path={`${match.path}/:experienceId?/menu/:slug?`}
              render={this.renderExperienceMenu(propsToPass)}
              key={2}
            />
          )}
          {match.params.offering && (
            <Route
              path={`${match.path}/:experienceId?/:slug?`}
              render={this.renderExperience(propsToPass)}
              key={2}
            />
          )}
        </Switch>
        <SearchModalLockContent isLocking={isLocking} />
      </>
    );
  }
}

export default connect<Props, ConnectProps>(withContext(SearchModalRoute), [
  'app',
  'calendar',
  'lock',
]);
